import { memo } from 'react'
import { IconButton, styled, Typography } from '@mui/material'
import { CloseIcon } from '$common/icons/close'
import { RemoveIcon } from '$common/icons/remove'
import { toLocale } from '$func/text'
import { ForwardIcon } from '$common/icons/forward'
import { CopyIcon } from '$common/icons/copy'
import { EditOutlinedIcon } from '$common/icons'
import { exitRoomFromSelectionMode } from '$logic/router'
import { ROOM_APP_BAR_HEIGHT } from '$common/room-app-bar/conf'
import { showUnableToShowContextMenuSnackbar } from '$comp/snackbars'

const Container = styled('div')<{ backgroundColor?: string }>(
  ({ theme, backgroundColor }) => ({
    backgroundColor: backgroundColor || theme.palette.neutral000,
    color: backgroundColor
      ? theme.palette.neutral000
      : theme.palette.neutral900,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 7px',
    height: `${ROOM_APP_BAR_HEIGHT}px`,

    flexShrink: '0',
    borderBottom: '1px solid',
    borderColor: theme.palette.neutral300,
  }),
)

const RowDirectionStack = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 100%;
`

const StyledIconButton = styled(IconButton)<{ 'data-is-disable'?: boolean }>`
  padding: 12px;
  height: 40px;
  width: 40px;
  &[data-is-disable='true'] {
    pointer-events: none;
  }
`

const StyledCloseIconButton = styled(IconButton)`
  padding: 12px;
  /* @noflip */
  margin-right: 18px;
`

interface Props {
  selectionCount: number
  canDelete: boolean
  canEdit: boolean
  canCopy: boolean
  canForward: boolean
  onForward: () => unknown
  onEdit: () => unknown
  onDelete: () => unknown
  onCopy: () => unknown
  onBack?: () => unknown
  arePermissionsStale?: boolean
  protectContent: boolean
  isGroup: boolean
  backgroundColor?: string
}
export const RoomAppBarSelection = memo(function RoomAppBarSelectionComponent({
  selectionCount,
  canEdit,
  canCopy,
  canDelete,
  canForward,
  onForward,
  onEdit,
  onDelete,
  onCopy,
  onBack,
  arePermissionsStale,
  protectContent,
  isGroup,
  backgroundColor,
}: Props) {
  return (
    <Container dir="rtl" backgroundColor={backgroundColor}>
      <RowDirectionStack>
        {canForward && (
          <StyledIconButton
            sx={{ opacity: protectContent ? 0.5 : 1 }}
            onClick={
              protectContent
                ? () => showUnableToShowContextMenuSnackbar(isGroup)
                : onForward
            }
            data-is-disable={arePermissionsStale}
          >
            <ForwardIcon sx={{ color: 'inherit' }} />
          </StyledIconButton>
        )}

        {canCopy && (
          <StyledIconButton
            sx={{ opacity: protectContent ? 0.5 : 1 }}
            onClick={
              protectContent ? showUnableToShowContextMenuSnackbar : onCopy
            }
            data-is-disable={arePermissionsStale}
          >
            <CopyIcon sx={{ color: 'inherit' }} />
          </StyledIconButton>
        )}

        {canDelete && (
          <StyledIconButton
            onClick={onDelete}
            data-is-disable={arePermissionsStale}
          >
            <RemoveIcon sx={{ color: 'inherit' }} />
          </StyledIconButton>
        )}

        {selectionCount === 1 && canEdit && (
          <StyledIconButton
            onClick={onEdit}
            data-is-disable={arePermissionsStale}
          >
            <EditOutlinedIcon sx={{ color: 'inherit' }} />
          </StyledIconButton>
        )}
      </RowDirectionStack>

      <RowDirectionStack>
        <Typography variant="subtitle1" color="inherit">
          {toLocale(selectionCount)}
        </Typography>

        <StyledCloseIconButton onClick={onBack || exitRoomFromSelectionMode}>
          <CloseIcon sx={{ color: 'inherit' }} />
        </StyledCloseIconButton>
      </RowDirectionStack>
    </Container>
  )
})
